const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 1920;

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("This image format cannot be processed. Please use JPEG, PNG, or a screenshot."));
    };
    image.src = url;
  });

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("Unable to compress this image.")),
      "image/jpeg",
      quality
    );
  });

export const prepareImageForUpload = async (file) => {
  if (!file || file.size <= MAX_IMAGE_BYTES) return file;

  const image = await loadImage(file);
  const scale = Math.min(
    1,
    MAX_IMAGE_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight)
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);

  let quality = 0.82;
  let blob = await canvasToBlob(canvas, quality);
  while (blob.size > MAX_IMAGE_BYTES && quality > 0.42) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, quality);
  }

  if (blob.size > MAX_IMAGE_BYTES) {
    throw new Error("The image is still too large. Please select a smaller image or screenshot.");
  }

  const baseName = file.name.replace(/\.[^.]+$/, "") || "asset-image";
  return new File([blob], `${baseName}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

export const handleImageSelection = async (event, setter) => {
  const file = event.target.files?.[0];
  if (!file) return;

  try {
    setter(await prepareImageForUpload(file));
  } catch (error) {
    event.target.value = "";
    alert(error.message || "Unable to prepare this image for upload.");
  }
};
